// Pure rule evaluator used by the action runner's `validate` step.
// Reads validations defined on each PageInput (in types.ts) and reports any
// rules that fail given the current value.

import type { PageInput, ValidationRule, ValidationRuleKind } from '@twasta/page-schema'

export interface FieldValidationError {
  name: string                   // input name
  message: string                // user-facing message
  rule: ValidationRuleKind       // which rule fired (for analytics/debug)
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const URL_RE = /^https?:\/\/[^\s/$.?#].[^\s]*$/i

function defaultMessage(rule: ValidationRule, label: string): string {
  switch (rule.rule) {
    case 'required':   return `${label} is required`
    case 'email':      return `${label} must be a valid email`
    case 'url':        return `${label} must be a valid URL`
    case 'number':     return `${label} must be a number`
    case 'regex':      return `${label} has an invalid format`
    case 'min_length': return `${label} must be at least ${rule.arg} characters`
    case 'max_length': return `${label} must be at most ${rule.arg} characters`
    default:           return `${label} is invalid`
  }
}

function evaluateOne(rule: ValidationRule, value: string): boolean {
  // Returns true if the rule PASSES.
  switch (rule.rule) {
    case 'required':
      return value !== undefined && value !== null && String(value).trim().length > 0
    case 'email':
      return value === '' || EMAIL_RE.test(value)
    case 'url':
      return value === '' || URL_RE.test(value)
    case 'number':
      return value === '' || !Number.isNaN(Number(value))
    case 'regex': {
      const pattern = String(rule.arg ?? '')
      if (!pattern) return true
      try { return value === '' || new RegExp(pattern).test(value) }
      catch { return true }      // bad pattern → don't fail validation
    }
    case 'min_length':
      return value === '' || value.length >= Number(rule.arg ?? 0)
    case 'max_length':
      return value.length <= Number(rule.arg ?? Infinity)
    default:
      return true
  }
}

// Evaluate a single input against its validations array.
export function evaluateInput(
  input: PageInput,
  value: string,
): FieldValidationError[] {
  const rules = input.validations || []
  const out: FieldValidationError[] = []
  for (const rule of rules) {
    if (!evaluateOne(rule, value ?? '')) {
      out.push({
        name: input.name,
        message: rule.message || defaultMessage(rule, input.label || input.name),
        rule: rule.rule,
      })
      // Stop at first failure per input — author's order = priority.
      break
    }
  }
  return out
}

// Evaluate a set of inputs (or all of them, if `names` is omitted).
export function evaluateInputs(
  inputs: PageInput[],
  values: Record<string, unknown>,
  names?: string[],
): FieldValidationError[] {
  const target = names && names.length
    ? inputs.filter((i) => names.includes(i.name))
    : inputs
  const out: FieldValidationError[] = []
  for (const inp of target) {
    const raw = values[inp.name]
    // Validation rules operate on text values; coerce non-strings so
    // built-in rules (required, min_length, regex, etc.) still apply.
    const text = raw == null ? '' : typeof raw === 'string' ? raw : (typeof raw === 'object' ? JSON.stringify(raw) : String(raw))
    out.push(...evaluateInput(inp, text))
  }
  return out
}
